import React, {
 useRef,
} from 'react';
import {
  useGetModifierList,
} from 'views/EMR/components/ServiceLookup/hooks';
import {
  ModifierListSettingDTO,
} from 'dtos/modifierListSettingDTO';
import {
  ModifierSuggestion,
} from 'views/EMR/components/ServiceLookup/modifierSuggestionRules';
import PopulateLookup from 'core/components/Lookup';
import ToolTip from 'core/components/Tooltip';
import isNil from 'lodash/isNil';
import {
  isEmptyString,
} from '@populateui/utils';
import {
 DEFAULT_EMPTY_VALUE,
} from 'core/constants';

const defaultSuggestions: ModifierSuggestion[] = [];

function getModifierCode(value: string | ModifierListSettingDTO | null | undefined) {
  return typeof value === 'string' ? value : value?.code;
}

function getModifierDescription(value: string | ModifierListSettingDTO | null | undefined) {
  return typeof value === 'string' ? value : value?.description;
}

function getFilteredModifiers(
  options: readonly ModifierListSettingDTO[],
  searchValue: string,
  suggestions: ModifierSuggestion[],
): ModifierListSettingDTO[] {
  const normalizedSearch = searchValue.toLowerCase();
  const filtered = [...options].filter(
    (modifier) => modifier?.code?.toLowerCase().includes(normalizedSearch),
  );

  if (!isEmptyString(searchValue)) {
    return filtered;
  }

  const suggestedOptions = suggestions.map((suggestion) => {
    const fromList = options.find(
      (modifier) => modifier.code?.toLowerCase() === suggestion.code.toLowerCase(),
    );

    return {
      code: suggestion.code,
      description: fromList?.description ?? suggestion.description,
    };
  });
  const suggestedCodes = new Set(
    suggestedOptions.map((option) => option.code?.toLowerCase() ?? ''),
  );
  const remaining = filtered.filter(
    (modifier) => !suggestedCodes.has(modifier.code?.toLowerCase() ?? ''),
  );

  return [
    ...suggestedOptions,
    ...remaining,
  ];
}

type ModifierOptionRowProps = {
  option: ModifierListSettingDTO;
  suggestion?: ModifierSuggestion;
};

function ModifierOptionRow({
  option,
  suggestion,
}: ModifierOptionRowProps) {
  const isSuggested = !isNil(suggestion);
  const description = getModifierDescription(option);
  const ref = useRef<HTMLDivElement>(null);

  return (
    <ToolTip
      title={isSuggested ? (
        <div className="flex flex-col gap-1 max-w-[400px]">
          <span className="font-semibold">
            {suggestion?.code}
            {' - '}
            {description}
          </span>
          <span>
            {suggestion?.description}
          </span>
        </div>
      ) : description}
      auto={!isSuggested}
      ref={ref}
      hide={!isSuggested}
    >
      <div className="flex flex-col items-start justify-start w-full text-left gap-2">
        <div className="flex flex-row items-center justify-start w-full gap-1 ">
          <span className="font-semibold">
            {option?.code ?? DEFAULT_EMPTY_VALUE}
          </span>
          {' - '}
          <span className="font-normal truncate" ref={ref}>
            {description}
          </span>
          {isSuggested && (
            <span className="w-fit rounded-full px-1.5 py-0.5 text-[10px] font-semibold tracking-wide bg-blue-100 text-blue-600">
              Suggested
            </span>
          )}
        </div>
      </div>
    </ToolTip>
  );
}

type Props = {
  value: ModifierListSettingDTO;
  onChange: (value: ModifierListSettingDTO) => void;
  onClose?: () => void;
  suggestions?: ModifierSuggestion[];
};

export default function ModifierLookup({
  value,
  onChange,
  onClose,
  suggestions = defaultSuggestions,
}: Props) {
  const { modifierList, isLoading } = useGetModifierList();
  const suggestionsByCode = new Map(
    suggestions.map((suggestion) => [suggestion.code.toLowerCase(), suggestion]),
  );

  const onAddModifier = (searchValue: string) => {
    const option = {
      code: searchValue,
      description: searchValue,
    };
    return option as ModifierListSettingDTO;
  };

  return (
    <PopulateLookup<ModifierListSettingDTO>
      value={value}
      onChange={(newValue) => {
        onChange(newValue as ModifierListSettingDTO);
        onClose?.();
      }}
      options={modifierList}
      isLoading={isLoading}
      disableClearable
      isOptionEqualToValue={(option, value) => getModifierCode(option) === getModifierCode(value)}
      placeholder="Search modifiers"
      type="modifier"
      minSearchSize={0}
      inputProps={{ minLength: 2, maxLength: 2 }}
      customFilterOptions={(
        options: readonly ModifierListSettingDTO[],
        searchValue: string,
      ) => getFilteredModifiers(options, searchValue, suggestions)}
      getUniqueId={(value) => getModifierCode(value)?.toLowerCase() ?? ''}
      getLabel={(value) => getModifierCode(value) ?? ''}
      onAddNew={onAddModifier as (value: string) => Promise<ModifierListSettingDTO>}
      classes={{ listbox: '!max-h-[180px]' }}
      renderOptionRow={(option) => (
        <ModifierOptionRow
          option={option}
          suggestion={suggestionsByCode.get(getModifierCode(option)?.toLowerCase() ?? '')}
        />
      )}
    />
  );
}
